import Flex from '@Components/Bases/Flex';
import { useCreateReport } from '@Domains/Reports';
import AssignmentLateOutlinedIcon from '@mui/icons-material/AssignmentLateOutlined';
import { Box, Button, Dialog, Typography } from '@mui/material';
import { getBuddhistYear } from '@Utils/datetime';
import { HttpStatusCode } from 'axios';
import dayjs, { Dayjs } from 'dayjs';
import { FormProvider, useForm } from 'react-hook-form';
import { useProfile } from 'src/Constant/UserProfileContext/UserProfileProvider';
import { ReportType } from 'src/Enum/Report';
import DurationYearForm from './DurationYearForm';
import { useSubmitDisabled } from './utils';

export type CreateReportFormValues = {
  startOperatingYear: Dayjs | null;
  endOperatingYear: Dayjs | null;
  symbol: string;
  companyId: string;
  ICompany: number;
  reportType: ReportType;
};

type CreateReportModalProps = {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  companyName?: string;
  companyEngName?: string;
};

const defaultValues = {
  reportType: ReportType.SINGLE,
  startOperatingYear: dayjs().startOf('year'),
  endOperatingYear: null,
};
const CreateReportModal = (props: CreateReportModalProps) => {
  const { user } = useProfile();
  const { open, onClose, onSuccess } = props;

  const modalCreateReportContext = useForm<CreateReportFormValues>({
    mode: 'onChange',
    defaultValues: defaultValues,
  });
  const { watch, reset } = modalCreateReportContext;
  const selectedYear = watch('reportType');
  const startOperatingYear = watch('startOperatingYear');
  const endOperatingYear = watch('endOperatingYear');
  const [, executeCreateReport] = useCreateReport();

  const isSubmitDisabled = useSubmitDisabled(
    selectedYear,
    startOperatingYear,
    endOperatingYear
  );

  const handleSubmit = async () => {
    const response = await executeCreateReport({
      data: {
        startOperatingYear: startOperatingYear
          ? getBuddhistYear(startOperatingYear.year())
          : null,
        endOperatingYear:
          selectedYear === ReportType.RANGE && endOperatingYear
            ? getBuddhistYear(endOperatingYear.year())
            : null,
        symbol: user?.E_COMPANY,
        companyId: user?.I_COMPANY_ID,
        iCompany: user?.I_COMPANY,
        reportType: selectedYear,
      },
    });
    if (response.status === HttpStatusCode.Created) {
      onSuccess?.();
      onClose();
    }
  };

  const handleClose = () => {
    reset(defaultValues);
    onClose();
  };
  return (
    <FormProvider {...modalCreateReportContext}>
      <Dialog open={open}>
        <form
          onSubmit={modalCreateReportContext.handleSubmit(handleSubmit)}
          noValidate
        >
          <Flex flexDirection={'column'} p={2} gap={2}>
            <Flex gap={1.5} borderBottom="1px solid rgba(0, 0, 0, 0.12)" pb={2}>
              <AssignmentLateOutlinedIcon
                fontSize="large"
                sx={{ color: '#F68809' }}
              />
              <Typography fontWeight="bold">
                สร้างแบบรายงาน Corporate Value Up
              </Typography>
            </Flex>

            <Typography fontWeight="bold">ชื่อบริษัท (ภาษาไทย)</Typography>
            <Box
              fontSize={14}
              p={1.5}
              bgcolor={'hsl(220, 2%, 90%)'}
              color={'text.primary'}
              borderRadius={1}
            >
              {user?.N_COMPANY_THAI}
            </Box>

            <Typography fontWeight="bold">ชื่อบริษัท (ภาษาอังกฤษ)</Typography>
            <Box
              fontSize={14}
              p={1.5}
              bgcolor={'hsl(220, 2%, 90%)'}
              color={'text.primary'}
              borderRadius={1}
            >
              {user?.N_COMPANY_ENG}
            </Box>
            <DurationYearForm />

            <Flex justifyContent={'end'} gap={2}>
              <Button onClick={handleClose} variant="contained" color="inherit">
                ยกเลิก
              </Button>
              <Button
                variant="contained"
                color="primary"
                type="submit"
                disabled={isSubmitDisabled}
              >
                ยืนยัน
              </Button>
            </Flex>
          </Flex>
        </form>
      </Dialog>
    </FormProvider>
  );
};

export default CreateReportModal;
